/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { CSSProperties } from 'react'

import type {
  ClassificationBoundingBox,
  SubmitClassificationBatchInput,
} from './classificationTypes'

export type NaturalImageSize = {
  width: number
  height: number
}

export type ViewportSize = {
  width: number
  height: number
}

type PixelBox = {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

export const REVIEW_DURATION_MS = 5000
const CROP_CONTEXT_PADDING_RATIO = 0.08
const CROP_MIN_CONTEXT_PADDING_PX = 4

const isNormalizedCoordinate = (value: number) => value >= 0 && value <= 1.5

export const boxUsesNormalizedCoordinates = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
) => [box.minX, box.maxX, box.minY, box.maxY].every(isNormalizedCoordinate)

export const getBoxAspectRatio = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
) => {
  const width = Math.max(box.maxX - box.minX, 0.01)
  const height = Math.max(box.maxY - box.minY, 0.01)

  return width / height
}

export const toPixelBox = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  naturalSize: NaturalImageSize,
): PixelBox => {
  if (boxUsesNormalizedCoordinates(box)) {
    return {
      minX: box.minX * naturalSize.width,
      maxX: box.maxX * naturalSize.width,
      minY: box.minY * naturalSize.height,
      maxY: box.maxY * naturalSize.height,
    }
  }

  return {
    minX: box.minX,
    maxX: box.maxX,
    minY: box.minY,
    maxY: box.maxY,
  }
}

export const getCropImageStyle = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  naturalSize: NaturalImageSize,
  viewport: ViewportSize,
): CSSProperties => {
  const { adjustedMinX, adjustedMinY, paddedBoxWidth, paddedBoxHeight } =
    getCropFrame(box, naturalSize, viewport)
  const scale = Math.min(
    viewport.width / paddedBoxWidth,
    viewport.height / paddedBoxHeight,
  )
  const scaledWidth = naturalSize.width * scale
  const scaledHeight = naturalSize.height * scale
  const centeredOffsetX = (viewport.width - paddedBoxWidth * scale) / 2
  const centeredOffsetY = (viewport.height - paddedBoxHeight * scale) / 2

  return {
    position: 'absolute',
    inset: 0,
    width: scaledWidth,
    height: scaledHeight,
    maxWidth: 'none',
    transform: `translate(${centeredOffsetX - adjustedMinX * scale}px, ${centeredOffsetY - adjustedMinY * scale}px)`,
    transformOrigin: 'top left',
    userSelect: 'none',
    pointerEvents: 'none',
  }
}

const getCropFrame = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  naturalSize: NaturalImageSize,
  viewport: ViewportSize,
) => {
  const pixelBox = toPixelBox(box, naturalSize)
  const boxWidth = Math.max(pixelBox.maxX - pixelBox.minX, 1)
  const boxHeight = Math.max(pixelBox.maxY - pixelBox.minY, 1)
  const aspectRatio = viewport.width / Math.max(viewport.height, 1)
  const contextPadding = Math.max(
    CROP_MIN_CONTEXT_PADDING_PX,
    Math.max(boxWidth, boxHeight) * CROP_CONTEXT_PADDING_RATIO,
  )
  const paddedContentWidth = boxWidth + contextPadding * 2
  const paddedContentHeight = boxHeight + contextPadding * 2
  const paddedBoxWidth = Math.min(
    naturalSize.width,
    Math.max(paddedContentWidth, paddedContentHeight * aspectRatio),
  )
  const paddedBoxHeight = Math.min(
    naturalSize.height,
    Math.max(paddedContentHeight, paddedBoxWidth / Math.max(aspectRatio, 0.01)),
  )
  const boxCenterX = (pixelBox.minX + pixelBox.maxX) / 2
  const boxCenterY = (pixelBox.minY + pixelBox.maxY) / 2
  const clampedMinX = Math.max(0, boxCenterX - paddedBoxWidth / 2)
  const clampedMinY = Math.max(0, boxCenterY - paddedBoxHeight / 2)
  const clampedMaxX = Math.min(naturalSize.width, clampedMinX + paddedBoxWidth)
  const clampedMaxY = Math.min(
    naturalSize.height,
    clampedMinY + paddedBoxHeight,
  )
  const adjustedMinX = Math.max(0, clampedMaxX - paddedBoxWidth)
  const adjustedMinY = Math.max(0, clampedMaxY - paddedBoxHeight)

  return {
    adjustedMinX,
    adjustedMinY,
    paddedBoxWidth,
    paddedBoxHeight,
    pixelBox,
  }
}

export const getCropOverlayStyle = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  naturalSize: NaturalImageSize,
  viewport: ViewportSize,
): CSSProperties => {
  const {
    adjustedMinX,
    adjustedMinY,
    paddedBoxWidth,
    paddedBoxHeight,
    pixelBox,
  } = getCropFrame(box, naturalSize, viewport)
  const scale = Math.min(
    viewport.width / paddedBoxWidth,
    viewport.height / paddedBoxHeight,
  )
  const centeredOffsetX = (viewport.width - paddedBoxWidth * scale) / 2
  const centeredOffsetY = (viewport.height - paddedBoxHeight * scale) / 2

  return {
    position: 'absolute',
    left: centeredOffsetX + (pixelBox.minX - adjustedMinX) * scale,
    top: centeredOffsetY + (pixelBox.minY - adjustedMinY) * scale,
    width: Math.max((pixelBox.maxX - pixelBox.minX) * scale, 2),
    height: Math.max((pixelBox.maxY - pixelBox.minY) * scale, 2),
    border: '2px solid rgba(255, 245, 230, 0.95)',
    boxShadow:
      '0 0 0 1px rgba(255, 107, 44, 0.65), 0 0 18px rgba(255, 107, 44, 0.18)',
    borderRadius: 4,
    pointerEvents: 'none',
  }
}

export const getOverlayBoxStyle = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  naturalSize: NaturalImageSize,
  renderedSize: ViewportSize,
): CSSProperties => {
  const pixelBox = toPixelBox(box, naturalSize)
  const scaleX = renderedSize.width / naturalSize.width
  const scaleY = renderedSize.height / naturalSize.height

  return {
    position: 'absolute',
    left: pixelBox.minX * scaleX,
    top: pixelBox.minY * scaleY,
    width: Math.max((pixelBox.maxX - pixelBox.minX) * scaleX, 2),
    height: Math.max((pixelBox.maxY - pixelBox.minY) * scaleY, 2),
    border: '3px solid #ff6b2c',
    boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.45)',
    borderRadius: 0,
    pointerEvents: 'none',
  }
}

export const getRenderedBoxRect = (
  box: Pick<ClassificationBoundingBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  naturalSize: NaturalImageSize,
  renderedSize: ViewportSize,
) => {
  const pixelBox = toPixelBox(box, naturalSize)
  const scaleX = renderedSize.width / naturalSize.width
  const scaleY = renderedSize.height / naturalSize.height

  return {
    left: pixelBox.minX * scaleX,
    top: pixelBox.minY * scaleY,
    width: Math.max((pixelBox.maxX - pixelBox.minX) * scaleX, 2),
    height: Math.max((pixelBox.maxY - pixelBox.minY) * scaleY, 2),
  }
}

export const buildClassificationSubmission = (
  upc: string,
  boxes: ClassificationBoundingBox[],
  rejectedIds: Set<string>,
  replacementUpc?: string | null,
  classificationReviewBatchId?: string | null,
): SubmitClassificationBatchInput => {
  const normalizedReplacementUpc =
    replacementUpc && replacementUpc !== upc ? replacementUpc : null

  return {
    upc,
    ...(normalizedReplacementUpc
      ? { replacementUpc: normalizedReplacementUpc }
      : {}),
    ...(classificationReviewBatchId ? { classificationReviewBatchId } : {}),
    acceptedBoxIds: boxes
      .filter(box => !rejectedIds.has(box.id))
      .map(box => box.id),
    rejectedBoxIds: boxes
      .filter(box => rejectedIds.has(box.id))
      .map(box => box.id),
  }
}
